import { createContext, ReactElement, ReactNode, useContext } from "react";

type FeatureFlags = string

export type Flag = {
  name: FeatureFlags;
  isActive: boolean;
};

export type Flags = Flag[];

export const FeatureFlagsContext = createContext<Flags>([]);

// Context provider
export function FlagsContextProvider({
  flags = [],
  children,
}: {
  flags: Flags;
  children: ReactNode;
}) {
  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

// Custom Hook API
export function useFlags(): Flags {
  return useContext(FeatureFlagsContext);
}

// Custom Hook API
export function useFlag(name: FeatureFlags): boolean {
  const features = useFlags();
  if (Array.isArray(features)) {
    const matchedFeature = features.filter(
      (feature) => feature.isActive && feature.name === name
    );

    return matchedFeature.length > 0;
  }
  return false;
}

// High Order Component API
export function Flags({
  authorizedFlags,
  exactFlags = false,
  renderOn = <></>,
  renderOff = <></>,
  children,
}: {
  authorizedFlags: FeatureFlags[];
  exactFlags?: boolean;
  renderOn?: ReactElement;
  renderOff?: ReactElement;
  children?: ReactElement;
}) {
  const matchedFlags = matchingFlags(useFlags(), authorizedFlags);

  if (exactFlags) {
    return matchedFlags.length === authorizedFlags.length
      ? children || renderOn
      : renderOff;
  }

  return matchedFlags.length ? children || renderOn : renderOff;
}

export function matchingFlags(flags: Flag[], listOfFlags: FeatureFlags[]) {
  return flags.filter((flag) => {
    return flag.isActive && listOfFlags.includes(flag.name);
  });
}
